import { CircularProgress } from "@mui/material";
import { isEmpty } from "lodash";
import { lazy, PropsWithChildren, Suspense } from "react";
import { useQuery } from "react-query";
import { Navigate, Route, Routes } from "react-router-dom";

import useAuth from "../contexts/AuthContext";
import { DashboardLayout } from "../layout/dashboard";
import { UserRoleEnum } from "../lib/auth";
import { getMaintenanceStatus } from "../lib/openEndpoints";
import LoginOnBehalfOf from "../pages/admin/LoginOnBehalfOf";

import { App } from "../pages/app";
import MaintenancePage from "../pages/openPages/MaintenancePage";
import { PrivacyPolicyPage } from "../pages/openPages/PrivacyPolicy";
import { TermsPage } from "../pages/openPages/TermsOfUse";
import SeamlessLogin from "../pages/seamlessLogin/SeamlessLogin";
import { SubscriptionPageTabs } from "../pages/subscription/SubscriptionPage";

const AccountsPage = lazy(() => import("../pages/accounts/AccountsPage"));
const CreateAccount = lazy(() => import("../pages/accounts/CreateAccount"));
const AccountList = lazy(() => import("../pages/accounts/SelectAccount"));
const AddBuoy = lazy(() => import("../pages/buoy/AddBuoy"));
const BuoyList = lazy(() => import("../pages/buoy/BuoyList"));
const BuoysPage = lazy(() => import("../pages/buoy/BuoysPage"));
const IntegrationsPage = lazy(() => import("../pages/integrations/IntegrationsPage"));
const IntegrationsGuide = lazy(() => import("../pages/integrations/IntegrationsGuide"));
const IntegrationsList = lazy(() => import("../pages/integrations/IntegrationsList"));
const LoginScreen = lazy(() => import("../pages/LoginScreen"));
const LogoutScreen = lazy(() => import("../pages/LogoutScreen"));
const ContactUsPage = lazy(() => import("../pages/openPages/ContactUsPage"));
const LandingPage = lazy(() => import("../pages/openPages/LandingPage"));
const Pricing = lazy(() => import("../pages/openPages/PricingLanding"));
const SalesConditions = lazy(() => import("../pages/openPages/SalesConditions"));
const ProfilePage = lazy(() => import("../pages/profile/ProfilePage"));
const RegisterProfile = lazy(() => import("../pages/profile/RegisterProfile"));
const ChangeSubscription = lazy(() => import("../pages/subscription/ChangeSubscription"));
const PurchaseSubscription = lazy(() => import("../pages/subscription/PurchaseSubscription"));
const PaymentCancelled = lazy(() => import("../pages/subscription/PaymentCancelled"));
const PaymentSuccess = lazy(() => import("../pages/subscription/PaymentSuccess"));
const SubscriptionPage = lazy(() => import("../pages/subscription/SubscriptionPage"));
const UsersPage = lazy(() => import("../pages/users/UsersPage"));
const NotFoundPage = lazy(() => import("../components/errors/404NotFound"));

function Router() {
	const { data: isMaintenanceActive, isLoading } = useQuery("maintenanceStatus", getMaintenanceStatus, {
		retry: false,
		refetchInterval: 60_000,
		refetchOnWindowFocus: true,
	});

	if (isLoading) return <CircularProgress />;
	if (isMaintenanceActive) return <MaintenancePage />;

	return (
		<Routes>
			<Route path="/" element={<LandingPage />}>
				<Route path=":buoyId" element={null} />
			</Route>

			<Route path="/app" element={<App />} />

			<Route path="/admin/login-behalf-of">
				<Route path=":token" element={<LoginOnBehalfOf />} />
			</Route>

			<Route path="/seamless-login" element={<SeamlessLogin />} />

			<Route path="/pricing" element={<Pricing />} />
			<Route path="/sales-conditions" element={<SalesConditions />} />
			<Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
			<Route path="/contact-us" element={<ContactUsPage />} />
			<Route path="/login" element={<LoginScreen register={false} />} />
			<Route path="/register" element={<LoginScreen register={true} />} />
			<Route
				path="/register/profile"
				element={
					<RequireAuth>
						<RegisterProfile />
					</RequireAuth>
				}
			/>
			<Route
				path="/accounts"
				element={
					<RequireAuth>
						<AccountsPage />
					</RequireAuth>
				}
			>
				<Route index element={<AccountList />} />
				<Route path="create" element={<CreateAccount />} />
			</Route>
			<Route path="/account" element={<DashboardLayout />}>
				<Route index element={<Navigate to="buoys" replace={true} />} />
				<Route path="buoys" element={<BuoysPage />}>
					<Route index element={<Navigate to="all" replace={true} />} />
					<Route
						path="add"
						element={
							<AccountOwnerOnly>
								<AddBuoy />
							</AccountOwnerOnly>
						}
					/>
					<Route path=":tabRoute" element={<BuoyList />} />
				</Route>

				<Route
					path="subscription"
					element={
						<AccountOwnerOnly>
							<Suspense fallback={<CircularProgress />}>
								<SubscriptionPage />
							</Suspense>
						</AccountOwnerOnly>
					}
				>
					<Route index element={<Navigate to="subscriptionTab" replace={true} />} />
					<Route path="change/:subscriptionId" element={<ChangeSubscription />} />
					<Route path="purchase" element={<PurchaseSubscription />} />
					<Route path="pay/success/:invoiceId" element={<PaymentSuccess />} />
					<Route path="pay/cancel" element={<PaymentCancelled />} />
					<Route path=":tabRoute" element={<SubscriptionPageTabs />} />
				</Route>

				<Route path="integrations" element={<IntegrationsPage />}>
					<Route index element={<IntegrationsList />} />
					<Route path=":integration" element={<IntegrationsGuide />} />
				</Route>
				<Route
					path="users"
					element={
						<AccountOwnerOnly>
							<UsersPage />
						</AccountOwnerOnly>
					}
				/>
				<Route path="profile" element={<ProfilePage />}>
					<Route path=":tabRoute" element={null} />
				</Route>
			</Route>
			<Route
				path="/terms"
				element={
					<RequireAuth>
						<TermsPage />
					</RequireAuth>
				}
			/>
			<Route path="/logout" element={<LogoutScreen />} />
			<Route path="*" element={<NotFoundPage />} />
		</Routes>
	);
}

function RequireAuth({ children }: PropsWithChildren<{}>) {
	const { token } = useAuth();
	return <>{!isEmpty(token) ? children : <Navigate to="/login" />}</>;
}

function AllowRoles({ children, allowedRoles }: PropsWithChildren<{ allowedRoles: UserRoleEnum[] }>) {
	const { currentUserClaims } = useAuth();
	if (!currentUserClaims) return <CircularProgress />;
	return <>{allowedRoles.includes(currentUserClaims.role) ? children : <Navigate to="/account" />}</>;
}

function AccountOwnerOnly({ children }: PropsWithChildren<{}>) {
	return (
		<AllowRoles allowedRoles={[UserRoleEnum.OWNER, UserRoleEnum.FDIR_ADMIN, UserRoleEnum.FDIR]}>
			{children}
		</AllowRoles>
	);
}

export default () => {
	return (
		<Suspense fallback={<CircularProgress />}>
			<Router />
		</Suspense>
	);
};
